package navigation.settings.areascountries

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.runtime.*
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.ui.Modifier
import cafe.adriel.voyager.koin.koinScreenModel
import com.essntl.features.area.domain.model.AreaModel
import com.essntl.features.area.ui.addeditarea.AddEditAreaEvent
import com.essntl.features.area.ui.addeditarea.components.AddEditAreaDialog
import com.essntl.features.area.ui.allareas.AllAreasEvent
import com.essntl.features.area.ui.allareas.AllAreasNavigation
import com.essntl.features.area.ui.allareas.AllAreasScreen
import com.essntl.features.country.domain.model.CountryModel
import com.essntl.features.country.ui.addeditcountry.AddEditCountryEvent
import com.essntl.features.country.ui.addeditcountry.componenets.AddEditCountryDialog
import com.essntl.features.country.ui.allcountries.AllCountriesEvent
import com.essntl.features.country.ui.allcountries.AllCountriesNavigation
import com.essntl.features.country.ui.allcountries.AllCountriesScreen
import com.essntl.uikit.components.utils.spacing
import navigation.settings.areas.addeditarea.AddEditAreaScreenModel
import navigation.settings.areas.allareas.AllAreasScreenModel
import navigation.settings.countries.addeditcountry.AddEditCountryScreenModel
import navigation.settings.countries.allcountries.AllCountriesScreenModel
import navigation.utils.NavLinkScreen

class AreasCountriesScreeNav(
    private val onRefresh: (() -> Unit) -> Unit,
) : NavLinkScreen {
    override val defaultTitle: String get() = "Areas & Countries"

    @Composable
    override fun Content() {
        val allAreasScreenModel = koinScreenModel<AllAreasScreenModel>()
        val allAreasState by allAreasScreenModel.state.collectAsState()

        val allCountriesScreenModel = koinScreenModel<AllCountriesScreenModel>()
        val allCountriesState by allCountriesScreenModel.state.collectAsState()

        val addEditCountryScreenModel = koinScreenModel<AddEditCountryScreenModel>()
        val addEditCountryState by addEditCountryScreenModel.state.collectAsState()
        var openAddEditCountryDialog by rememberSaveable { mutableStateOf(false) }
        var countryToEdit by remember { mutableStateOf<CountryModel?>(null) }

        val addEditAreaScreenModel = koinScreenModel<AddEditAreaScreenModel>()
        val addEditAreaState by addEditAreaScreenModel.state.collectAsState()
        var openAddEditAreaDialog by rememberSaveable { mutableStateOf(false) }
        var areaToEdit by remember { mutableStateOf<AreaModel?>(null) }

        LaunchedEffect(Unit) {
            allAreasScreenModel.onEvent(AllAreasEvent.GetAllAreas)
            allCountriesScreenModel.onEvent(AllCountriesEvent.GetAllCountries)
            onRefresh {
                allAreasScreenModel.onEvent(AllAreasEvent.GetAllAreas)
                allCountriesScreenModel.onEvent(AllCountriesEvent.GetAllCountries)
            }
        }

        LaunchedEffect(addEditCountryState.addEditCountry.isSuccess) {
            if (addEditCountryState.addEditCountry.isSuccess) {
                addEditCountryState.addEditCountry.data?.let {
                    if (countryToEdit == null) {
                        allCountriesScreenModel.onEvent(AllCountriesEvent.AddCountry(it))
                    } else {
                        allCountriesScreenModel.onEvent(AllCountriesEvent.EditCountry(it))
                    }
                }
                addEditCountryScreenModel.onEvent(AddEditCountryEvent.ClearState)
                openAddEditCountryDialog = false
                countryToEdit = null
            }
        }

        LaunchedEffect(addEditCountryState.deleteCountry.isSuccess) {
            if (addEditCountryState.deleteCountry.isSuccess) {
                countryToEdit?.let {
                    allCountriesScreenModel.onEvent(AllCountriesEvent.DeleteCountry(it))
                    addEditCountryScreenModel.onEvent(AddEditCountryEvent.ClearState)
                    openAddEditCountryDialog = false
                    countryToEdit = null
                }
            }
        }

        LaunchedEffect(addEditAreaState.addCountry.isSuccess) {
            if (addEditAreaState.addCountry.isSuccess)
                addEditAreaState.addCountry.data?.let { c ->
                    allCountriesScreenModel.onEvent(AllCountriesEvent.AddCountry(c))
                }
        }

        LaunchedEffect(addEditAreaState.addEditArea.isSuccess) {
            if (addEditAreaState.addEditArea.isSuccess) {
                addEditAreaState.addEditArea.data?.let {
                    if (areaToEdit == null) {
                        allAreasScreenModel.onEvent(AllAreasEvent.AddArea(it))
                    } else {
                        allAreasScreenModel.onEvent(AllAreasEvent.EditArea(it))
                    }
                }
                addEditAreaScreenModel.onEvent(AddEditAreaEvent.ClearState)
                openAddEditAreaDialog = false
                areaToEdit = null
            }
        }

        LaunchedEffect(addEditAreaState.deleteArea.isSuccess) {
            if (addEditAreaState.deleteArea.isSuccess) {
                areaToEdit?.let {
                    allAreasScreenModel.onEvent(AllAreasEvent.DeleteArea(it))
                    addEditAreaScreenModel.onEvent(AddEditAreaEvent.ClearState)
                    openAddEditAreaDialog = false
                    areaToEdit = null
                }
            }
        }

        Column(
            verticalArrangement = Arrangement.spacedBy(3.spacing),
            modifier = Modifier.fillMaxSize(),
        ) {
            AllCountriesScreen(
                state = allCountriesState,
                onEvent = allCountriesScreenModel::onEvent,
                onNavigation = {
                    when (it) {
                        is AllCountriesNavigation.AddCountry -> {
                            openAddEditCountryDialog = true
                        }

                        is AllCountriesNavigation.EditCountry -> {
                            countryToEdit = it.country
                            openAddEditCountryDialog = true
                        }
                    }
                },
            )

            AllAreasScreen(
                state = allAreasState,
                onEvent = allAreasScreenModel::onEvent,
                onNavigation = {
                    when (it) {
                        is AllAreasNavigation.AddArea -> {
                            openAddEditAreaDialog = true
                        }

                        is AllAreasNavigation.EditArea -> {
                            areaToEdit = it.area
                            openAddEditAreaDialog = true
                        }
                    }
                },
            )
        }

        AddEditCountryDialog(
            openDialog = openAddEditCountryDialog,
            currentCountry = countryToEdit,
            state = addEditCountryState,
            validation = addEditCountryScreenModel.validation,
            onEvent = addEditCountryScreenModel::onEvent,
            onDismissRequest = {
                openAddEditCountryDialog = false
                countryToEdit = null
            },
        )

        AddEditAreaDialog(
            openDialog = openAddEditAreaDialog,
            currentArea = areaToEdit,
            state = addEditAreaState,
            validation = addEditAreaScreenModel.validation,
            onEvent = addEditAreaScreenModel::onEvent,
            onDismissRequest = {
                openAddEditAreaDialog = false
                areaToEdit = null
            },
        )
    }
}